import Config from 'config/settings';
import dev from 'config/dev';
import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { constrain } from 'utils/math';
import { SectionType, DepartmentType, ViewType, DirectionalKeyType } from 'app/data/Enums';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';

import { MouseController } from 'app/chrome/mouse/MouseController';
import { PixiController } from 'app/services/PixiController';

import { Paging } from 'app/module/global/Paging';

import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { HomeViewConfig } from 'app/model/HomeViewConfig';

import { ViewReadyEvent } from 'app/events/ViewReadyEvent';
import { DebugMouseWheelEvent } from 'app/events/DebugMouseWheelEvent';
import { DebugKeypressEvent } from 'app/events/DebugKeypressEvent';

import { Point } from 'app/contracts/Geometry';

import DelayHelper from 'utils/timers';
import { PreloadController } from 'app/chrome/preload/PreloadController';
import { UIController } from 'app/views/ui/UIController';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';
import SiteTransitions from 'app/services/SiteTransitions';
import { Simple } from 'utils/analytics';

import { HomeView } from './HomeView';
import { HomeScene } from './HomeScene';
import { HomeDrag } from './HomeDrag';
import { HomePaging } from './HomePaging';
import { HomePrompts } from './HomePrompts';
import { HomeLoading } from './HomeLoading';
import { device } from 'utils/device';

/**
 * Home section controller. Orchestrates the per-concern sub-objects in this
 * folder (see home.types.ts for the decomposition pattern, established in
 * src/app/views/work/). Keeps: the lifecycle hooks, the route entries, the
 * full public surface used by AppController / SiteTransitions /
 * NavMenuController / HomeTimeline, every Events.on(...) registration,
 * and all cross-controller access (this.get(...) / injected controllers).
 */
export class HomeController extends Controller {
	private _oldState: HistoryStateModel;
	private _active: boolean = false; //Is interative / active
	private _backgroundActive: boolean = true; //Is active as part of Work section
	private _isSetup: boolean = false; //Is setup up ready to view
	private _resetAndShowQued: boolean = false;

	private _mouseController: MouseController;
	private _pixiController: PixiController;
	private _preloadController: PreloadController;
	private _uiController: UIController;

	private _projectFilterVisible: boolean = false;

	//AJAX Loaded JSON
	private _json: HomeViewConfig;
	private _isDeeplink: boolean = false;

	private _innerWidth: number;
	private _innerHeight: number;
	private _virtualWidth: number;
	private _timeouts: any = {};
	private _tweens: any = {};

	private _viewPortCount: number;

	private _workDeeplinkDepartment: DepartmentType;

	private _showIntroPress: boolean = true;

	private _openingActionClicked: boolean = false;
	private $body: HTMLElement;
	//#pixi canvas wrapper — written once, never read (public so noUnusedLocals doesn't flag it):
	public $pixiContainer: Element;

	private _activeWorkDepartment: DepartmentType;

	//Per-concern sub-objects (see home.types.ts for the pattern):
	private _loading: HomeLoading;
	private _view: HomeView;
	private _scene: HomeScene;
	private _drag: HomeDrag;
	private _paging: HomePaging;
	private _prompts: HomePrompts;

	constructor(id: string) {
		super(id);

		this._loading = new HomeLoading();

		this._view = new HomeView(this.DOM, {
			onContainerReady: (container: Element) => {
				this.container = container as HTMLElement;
			},
			onExploreHover: (center: Point) => {
				this._scene.timeline.setButtonHover(center);
				this._mouseController.setHiddenState(true);
			},
			onExploreOut: (department: DepartmentType) => {
				this._scene.timeline.setButtonOut(department);
				this._mouseController.setHiddenState(false);
			}
		});

		this._scene = new HomeScene({
			isActive: () => this._active,
			onCompleteExit: () => this.DOM.removeClass(this.container, '-active')
		});

		this._drag = new HomeDrag({
			onFrame: (progressRatio: number) => {
				this._scene.timeline.setProgress(progressRatio);
			},
			onStartDrag: () => {
				this._scene.timeline.mouseDown();
				this._mouseController.setDragDown(true);
			},
			onStopDrag: () => {
				this._scene.timeline.mouseUp();
				this._mouseController.setDragDown(false);
			}
		});

		this._paging = new HomePaging({
			onPagingClick: (index: number) => this._pagingClick(index)
		});

		this._prompts = new HomePrompts({
			getProgress: () => this._drag.progress,
			onPromptNoFollowCursor: () => this.DOM.addClass(SiteTransitions.$body, '-prompt-no-follow-cursor')
		});
	}

	//Init is called when routed on /
	public init(): void {
		if ((window as any).skipIntroClick) {
			this._showIntroPress = false;
		}

		this.$pixiContainer = document.querySelector('#pixi');
		this.$body = document.querySelector('body');

		this._active = true;
		this._isDeeplink = true;

		//Set the opening state to the homepage:
		let openingState: HistoryStateModel = {
			section: SectionType.HOME,
			title: 'Interactive Screens & Spaces',
			href: '/'
		};

		Events.dispatch(AppEvents.SET_INTIAL_STATE, openingState);

		this._view.createTextScramble();
		this.get('Preload').textScrable = this._view.textScramble;

		this._view.bindExploreButton();

		Events.on('CHANGE_HOME_SCENE', (e) => {
			this._uiController.resetDragPrompt();

			if (e.scene == 'icon') {
				this._prompts.queueIconScenePrompt();
			}

			this._activeWorkDepartment = e.scene;
		});
	}

	//Getters ---------------------------------------------------------------------

	/** Get is section / controller is active */
	public isActive(): boolean {
		return this._active;
	}

	/** Get if ready to setup for view */
	public isReadyToSetup(): boolean {
		return this._loading.readyToSetup;
	}

	/** Check is ready to view. Really needs to check is 'ready to setup' */
	public isReadyToView(stateObject: HistoryStateModel): boolean {
		return this.isReadyToSetup();
	}

	public readyForDeeplink(): boolean {
		//If this function is being called then process is checking if it can show
		//So if its ready to setup but not actually been created then can setup here
		if (this._loading.readyToSetup && !this._isSetup) {
			this._setup();
		}

		return this._loading.readyToSetup && this._isSetup;
	}

	//Setters-----------------------------------------------------------------------

	//Set controller depencies (set from app controller):
	public setControllers(
		mouse: MouseController,
		pixi: PixiController,
		preloadController: PreloadController,
		ui: UIController
	): void {
		//Set references to required controllers:
		this._mouseController = mouse;
		this._pixiController = pixi;
		this._preloadController = preloadController;
		this._uiController = ui;

		this._prompts.setControllers(ui, mouse);
	}

	//Pixi dependecy (set in AppController):
	public setPixi($pixi: PIXI.Application, $homeContainer: PIXI.Container): void {
		this._scene.setPixi($pixi, $homeContainer);
	}

	/** History state has been changed by / back forward */
	public setState(state: HistoryStateModel): void {}

	/**
	/* HTML has loaded from external, so set as requried:
	* @param {string} html -  raw ajax loaded HTML
	*/
	public setHTML(html: string): void {
		this._view.storeHTML(html);
		this._loading.setFlag('dom');
		this._testSetup();
	}

	/**
	/* JSON config has loaded so set:
	* @param {HomeViewConfig} json -  JSON data for home view
	*/
	public setJSON(json: HomeViewConfig): void {
		this._json = json;
		this._setVirtualWidth();
		this._loading.setFlag('json');
		this._testSetup();
	}

	//Public-------------------------------------------------------------------------

	/** Reset is always called before showing to put in correct state. Seperate from 'show' as a deplay might be needed between the 2 */
	public reset(oldState: HistoryStateModel = null, isDeeplink: boolean = false): void {
		this._oldState = oldState;

		//If not setup then do
		if (!this._isSetup) {
			this._setup();
			this._resetAndShowQued = true;
			return;
		}

		//0.) Generic across all types of show:
		this._resetIntervalsAndAnimations();

		this._mouseController.setColour('light');

		if (this.container) {
			this.DOM.addClass(this.container, '-active');
			SiteTransitions.resetHome();
		}

		//Is a deeplink for get ready to show coming direct from Preoader
		if (isDeeplink) {
			this._resetForDeeplink();
			return;
		}

		//If transition is coming back from screens / spaces section
		if (oldState.section === SectionType.WORK) {
			this._resetFromWork(oldState.subsection as DepartmentType);
		}
	}

	/** Preloading is complete can be shown / activate dragging: */
	public show(oldState: HistoryStateModel = null, isDeeplink: boolean = false): void {
		if (!this.container) {
			return;
		}

		this._pixiController.unpause();

		if (isDeeplink) {
			this._showForDeeplink();
			return;
		}

		if (oldState.section === SectionType.WORK) {
			this._showFromWork(oldState.subsection as DepartmentType);
		}

		if (oldState.section === SectionType.ABOUT) {
			this._active = true;
			this.resize();
			setTimeout(() => {
				this.showDragPrompt();
			}, 300);
		}
	}

	/**
	 * Hide after section is moved away from. The new state is passed so can work out how to transition out
	 * @param {HistoryStateModel} newState
	 */
	public hide(newState: HistoryStateModel = null): void {
		if (newState.section === SectionType.ABOUT) {
			this._hideForInfo();
			this._active = false;
		} else if (newState.section === SectionType.WORK) {
			this._hideForWork(newState.subsection as DepartmentType);
		}
	}

	/** Freeze for when transitions are being used */
	public freeze(): void {
		this._active = false;
		this._setInteractionEnabled(false);
		this._resetIntervalsAndAnimations();
		this._scene.timeline.freeze();
		this._timeouts.freeze = setTimeout(() => this._cleanupAfterFreeze(), 500);
	}

	/** Pause (For when nav menu is showing) */

	public pauseForMenu(direction = 'side'): void {
		this._setInteractionEnabled(false);
		this._scene.timeline.freeze();

		if (direction == 'side') {
			this.DOM.addClass(this.$body, 'app-paused');
			this.DOM.addClass(this.$body, '-pausedside');
		} else {
			this.DOM.addClass(this.$body, 'app-paused');
		}

		this._pixiController.setAllowSlowFrameCheck(false);
	}

	/** Unpause (For when nav menu is hidden) */
	public unpause(): void {
		DelayHelper.unpause();

		if (globals.openingActionClicked) {
			this._setInteractionEnabled(true);
		}

		this._scene.timeline.unfreeze();

		this.DOM.removeClass(this.$body, 'app-paused');
		this.DOM.removeClass(this.$body, '-pausedside');

		this.get('Pixi').unpause();

		this._pixiController.setAllowSlowFrameCheck(true);

		this._mouseController.resetToHorizontal();
		this.get('ui').unpause();
	}

	/**  Modules are mounted to the DOM and ready to access */
	public modulesMounted(): void {
		if (this._active) {
			this._setupPaging();
		}
	}

	public pixiParticlesLoaded(spritesheet: PIXI.Spritesheet): void {
		this._scene.setParticlesSpritesheet(spritesheet);
		this._loading.setFlag('pixi');
		this._testSetup();
	}

	private _testSetup() {
		if (window.mode == 'lite') {
			return;
		}

		this._testReadyToSetup();
		if (this._loading.readyToSetup && (this._active || this._backgroundActive)) {
			this._setup();
		}
	}

	/** The logo morpth sprite has loaded so create that animation setup */

	/** DOM has loaded / ready so flag and setup as required */
	public DOMReady(): void {
		//If active then on home view, so the html will already be on the page
		//Otherwise create container elements to load section to

		if (this._active) {
			this._loading.setFlag('dom');
			this._testReadyToSetup();
			this._setup();
		}
	}

	public resize(skipRepeat = false): void {
		if (!this._active) {
			return;
		}

		if (this._json) {
			this._setVirtualWidth();
		}

		if (this._isSetup && this._drag.isCreated) {
			this._scene.timeline.setVirtualWidth(this._virtualWidth, this._viewPortCount);
			this._drag.setVirtualWidth(this._virtualWidth);
			this._scene.applyContainerResize(this._innerWidth, this._innerHeight, this._active);
		}

		if (skipRepeat) {
			return;
		}

		setTimeout(() => {
			if (this._isSetup && this._drag.isCreated) {
				this._scene.timeline.setVirtualWidth(this._virtualWidth, this._viewPortCount);
				this._drag.setVirtualWidth(this._virtualWidth);
				this._scene.applyContainerResize(this._innerWidth, this._innerHeight, this._active);
			}
		}, 100);
	}

	public tick(delta: number = 1, bypassCheck: boolean = false): void {
		if (!bypassCheck && (!this._active || !this._isSetup)) {
			return;
		}

		globals.scaleFactorM = 0.5 + 0.5 * (device.innerWidth / 1920);

		this._prompts.tickCounters();

		this._scene.container.scale.set(
			1.0 + 0.3 * Math.abs(this._drag.velocity),
			1.0 + 0.3 * Math.abs(this._drag.velocity)
		);

		if (this._scene.timeline) {
			this._scene.timeline.tick(
				this._mouseController.getMouseCords(),
				this._mouseController.mouseIsDown(),
				this._drag.velocity,
				false,
				false
			);
		}

		this._prompts.processNextPrompts();
		this._paging.processShowHide(this._drag.progress);
		this._processShowProjectsButton();
	}

	private _processShowProjectsButton() {
		if (this._projectFilterVisible) {
			return;
		}

		if (this._drag.progress > 0.2) {
			this._uiController.showUiItem('projects');
			this._projectFilterVisible = true;
		}
	}

	public clearPromptsOnWayToWork() {
		this._prompts.clearOnWayToWork();
	}

	public showDragPrompt() {
		this._prompts.showDragPrompt();
	}

	//The logo morpth sprite has loaded so create that animation setup

	/**
	 * Animate x to progress to a point in time
	 * @param {number} index -  break point Index to animate to (0 Screens | 1 Home | 2 Spaces)
	 */
	public animateProgressToValue(index: number): void {
		this._drag.setIndex(index);
		SiteTransitions.homeExploreMouseOut();
	}

	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number) {
		if (this._scene.timeline) {
			this._scene.timeline.updateWorkTransitionParticles(introXPx, introProgressRatio);
		}
	}

	public subsectionStateChange(state: HistoryStateModel): void {
		if (!state.workIndex && state.workIndex !== 0) {
			state.workIndex = 1;
		}

		//Just do as per paging click:
		this._pagingClick(state.workIndex);
	}

	public setPagingIndex(index: number) {
		this._drag.breakpointIndex = index;
		this._paging.setActiveIndex(index);
	}

	public isOnDepartment(workDeparment: DepartmentType) {
		if (workDeparment == DepartmentType.SCREENS) {
			return this._drag.progress < 0.03;
		}

		return this._drag.progress > 0.97;
	}

	public triggerExplore(workDeparment: DepartmentType) {
		this._onExploreButtonClick(null, workDeparment);
	}

	//private
	//----------------------------------------------------------------------
	//----------------------------------------------------------------------
	//----------------------------------------------------------------------
	//----------------------------------------------------------------------

	/** Work out the virtual container width of view. Used in timeline and drag logic */
	private _setVirtualWidth(): void {
		this._innerWidth = device.innerWidth;
		this._innerHeight = device.innerHeight;

		this._viewPortCount = this._json.containerViewportsDesktop;

		if (this._innerWidth < 1200) {
			this._viewPortCount = this._json.containerViewportsMobile;
		}

		this._virtualWidth = this._innerWidth * this._viewPortCount;
	}

	/** Test that the view is ready to show */
	private _testReadyToSetup(): void {
		let fireEvent = !this._loading.readyToSetup;
		this._loading.testReadyToSetup();

		//If ready for first time flag this:
		if (fireEvent && this._loading.readyToSetup) {
			//Trigger the VIEW_SETUP event, which can be picked up by any loading processes:
			Events.dispatch(
				new ViewReadyEvent({
					view: ViewType.HOME
				}),
				{ delay: 1 }
			);
		}
	}

	/** Check DOM / Pixi requirements are ready, if so can setup */
	private _setup(): void {
		if (!this._isDeeplink) {
			return;
		}

		if (!this._loading.readyToSetup || this._isSetup) {
			return;
		}

		//If page is not ready then get the
		if (!this._view.htmlLoading && !this.container && window.mode != 'lite') {
			this._view.injectHTML(() => this._setup());
			return;
		}

		this._isSetup = true;

		this._setVirtualWidth();
		this._setupPaging();
		this._addEvents();
		this._createTimelines();
		this._createBreakpoints();

		this.resize(true);

		//If reset and show qued then
		if (this._resetAndShowQued) {
			this.reset(this._oldState, false);
			this.show(this._oldState, false);
			this._resetAndShowQued = false;
		}

		//If not deep link then place at
		if (!this._isDeeplink) {
			this._setupForWork();
		}
	}

	public setWorkDepartmentType(department: DepartmentType) {
		this._workDeeplinkDepartment = department;
	}

	private _setupForWork(): void {
		return;

		this._scene.container.visible = true;
		this._scene.timeline.setupForWork(this._workDeeplinkDepartment);
		let progress = this._workDeeplinkDepartment === DepartmentType.SCREENS ? 0 : 1;
		this._resetXProgress(progress);
		this._scene.timeline.resetFromWork(this._workDeeplinkDepartment);
		this._scene.timeline.setActive(true);

		this._drag.setProgress(progress);
	}

	/** Setup paging */
	private _setupPaging(): void {
		if (window.mode == 'lite') {
			return;
		}

		//If active then paging module is already auto crated on the page:
		this._paging.setup(this._active ? (this.module.get('home-paging') as Paging) : null, this.container);
	}

	/** Set interactoon to on/off */
	private _setInteractionEnabled(enabled: boolean): void {
		this._drag.setActive(enabled);
		this._scene.setTimelineActive(enabled);
	}

	private _addEvents(): void {
		//Add the main explore clicks
		Events.on('click', '.home__explorelink', this._onExploreButtonClick.bind(this));
		Events.on(DebugMouseWheelEvent, this._onMouseWheel.bind(this));
		Events.on(DebugKeypressEvent, this._onKeyPress.bind(this));
	}

	private _pagingClick(index: number): void {
		this._drag.setIndex(index);
		this._paging.setActiveIndex(index);
	}

	private _createBreakpoints(): void {
		this._drag.createBreakpoints(this._virtualWidth, this._json, this._workDeeplinkDepartment);
	}

	//Create the different animations for x and y movement on home:
	private _createTimelines(): void {
		this._scene.createTimelines(
			this._mouseController,
			this._json,
			this.container,
			this._workDeeplinkDepartment,
			this._virtualWidth,
			this._viewPortCount,
			{
				setPagingIndex: (index: number) => this.setPagingIndex(index),
				hideDragPrompt: () => this.get('ui').hideDragPrompt()
			},
			{
				//The home WebGL layer's preload-join reports (ex the
				//globals.preloadController calls inside HomeTimeline —
				//PRELOAD HANDSHAKE: both MUST end up fired or the site never
				//leaves the loader):
				reportPixelDataReady: () => this._preloadController.homePixelDataReady(),
				reportSceneCreated: () => this._preloadController.homeParticlesCreated()
			}
		);
	}

	/** On explore press */
	private _onExploreButtonClick(e: MouseEvent = null, forceType: DepartmentType = null): boolean {
		let department: DepartmentType;

		console.log('Explore click');

		if (e) {
			e.preventDefault();
			let $link: Element = e.target as HTMLElement;
			this.DOM.addClass($link, '-lineclicked');
			department = $link.getAttribute('data-department') as DepartmentType;
		} else {
			department = forceType;
		}

		this._setInteractionEnabled(false);

		if (!department) {
			department = DepartmentType.SCREENS;
			if (this._scene.timeline.progress > 0.7) department = DepartmentType.SYSTEMS;
			else if (this._scene.timeline.progress > 0.4) department = DepartmentType.SPACES;
		}

		let title;
		if (department === DepartmentType.SCREENS) title = 'Screens';
		else if (department === DepartmentType.SPACES) title = 'Spaces';
		else if (department === DepartmentType.SYSTEMS) title = 'Systems';

		//Trigger event to switch to 'work' controller for department:
		let newState: HistoryStateModel = {
			section: SectionType.WORK,
			subsection: department,
			title: title,
			href: `/${department}`,
			workIndex: 0
		};
		Events.dispatch(AppEvents.CHANGE_SECTION, newState);

		Simple.event('home_explore_click', { department: department });

		return false;
	}

	//Scrollwheel / mouse press
	//------------------------------------------------------------------------------------
	private _wheelLocked = false;
	private timeout;
	private _onMouseWheel(event: DebugMouseWheelEvent): void {
		if (!event.isHuman || this._wheelLocked) {
			return;
		}

		let direction = -1 * event.delta;

		this._drag.breakpointIndex += direction;
		this.animateProgressToValue(constrain(this._drag.breakpointIndex, 0, 3));

		Simple.event('home_mousewheel', { breakPointIndex: this._drag.breakpointIndex });

		this._wheelLocked = true;

		if (this.timeout) clearTimeout(this.timeout);
		this.timeout = setTimeout(() => {
			this._wheelLocked = false;
			this.timeout = null;
		}, 200);
	}

	private _onKeyPress(event: DebugKeypressEvent): void {
		if (!this._active) {
			return;
		}
		let direction = event.keyType === DirectionalKeyType.LEFT || event.keyType === DirectionalKeyType.PAGE_UP ? -1 : 1;
		this._drag.breakpointIndex += direction;
		this.animateProgressToValue(constrain(this._drag.breakpointIndex, 0, 3));

		Simple.event('home_keypress', { direction: direction, breakPointIndex: this._drag.breakpointIndex });
	}

	/** After a deeplink / history pop() freeze then reset key things after hidden */
	private _cleanupAfterFreeze(): void {
		this._scene.container.visible = false;
		this._resetAndShowQued = false;
		this.DOM.removeClass(this.container, '-active');
	}

	//Reset methods
	//----------------------------------------------------------------------------------------------------------

	/** Reset for when coming from the full-screen preloader */
	private _resetForDeeplink(): void {
		//1.) Reset X positon of the timeline to the middle:
		this._resetXProgress(0);

		//2.) Reset main timeline:
		this._scene.timeline.resetForDeeplink();
	}

	/** Reset when coming back from work section (To animate the work items down from top) */
	private _resetFromWork(department: DepartmentType): void {
		//Run a tick that bypasses normal active check to setup any depencies :
		globals.hidingWork = false;
		this.tick(1, true);

		//1.) Set the progress / side to the corret department
		let progress: number = department === DepartmentType.SCREENS ? 0.3333333 : 0.6666666;
		if (department == DepartmentType.SYSTEMS) progress = 1;
		this._resetXProgress(progress);

		//2.) Reset the main timeline:
		this._scene.timeline.resetFromWork(department);
	}

	/** Remove all intervals and animations that may have been applied */
	private _resetIntervalsAndAnimations(): void {
		//1.) Remove any animations/intervals from this main view class:
		clearTimeout(this._timeouts.freeze);
		if (this._tweens.container) {
			this._tweens.container.kill();
		}

		//2.) Remove any animations/intervals from the main timeline:
		if (this._scene.timeline) {
			this._scene.timeline.resetIntervalsAndAnimations();
		}

		this._resetAndShowQued = false;
	}

	/** Reset the x progress of the timline */
	private _resetXProgress(progress: number): void {
		this._scene.timeline.setProgress(progress, true);
		//TODO: convert set progress to a 0-1 to ration
		this._drag.resetProgress(progress);

		//Do an 'instant' update on the timeline. This should move everything the current psoition
		let defaultMouseCords: any = { x: device.innerWidth / 2, y: device.innerHeight / 2 };
		this._scene.timeline.tick(defaultMouseCords, false, 0, true, true);
	}

	//Show methods
	//-------------------------------------------------------------------------------------------------------

	/** Show from when coming from the full-screen preloader */
	private _showForDeeplink(): void {
		//Allow interaction:

		if (!this._showIntroPress) {
			setTimeout(() => {
				this.resize(true);
				this._setupOpeningAction();
			}, 900);
			return;
		}

		if (this._preloadController.isFullyLoaded) {
			this._showClickableArea();
		} else {
			this._preloadController.onFullyLoaded = () => {
				this._showClickableArea();
			};
		}

		return;
		DelayHelper.setTimeout(() => {
			this._setupOpeningAction();

			this._mouseController.homeIntroStart();

			this.DOM.addClass(document.querySelector('.home__action__text'), 'loaded');

			//Build in the elements from

			this.resize();
		}, 10000);
	}

	private _showClickableArea() {
		DelayHelper.setTimeout(() => {
			this._setupOpeningAction();
			this._mouseController.homeIntroStart();

			this.DOM.addClass(document.querySelector('.home__action__text'), 'loaded');

			//Build in the elements from

			this.resize(true);
		}, 400);
	}

	//DelayHelper.setTimeout id (number | string). NOTE: it is also passed to the
	//NATIVE clearTimeout at the call sites, which does not cancel DelayHelper
	//timers — suspected pre-existing bug, left as-is:
	private _timeoutInt: any = 0;
	private _buttonEventIds: any = { mouseOver: null, mouseOut: null };

	private _setupOpeningAction(): void {
		if (!this._showIntroPress) {
			this._openingActionComplete();
			return;
		}

		this._mouseController.homeIntroStart();
		this._scene.timeline.homeIntroStart();

		DelayHelper.setTimeout(() => {
			this._view.textScramble.setText('Interact');
		}, 300);

		if (globals.inCircleLoader)
			gsap.TweenMax.fromTo(
				'.home__action',
				0,
				{ autoAlpha: 0, scale: 0, y: -75, x: -75 },
				{ autoAlpha: 1, scale: 1, x: 0, y: 0, delay: 0 }
			);
		else
			gsap.TweenMax.fromTo(
				'.home__action',
				0.45,
				{ autoAlpha: 0, scale: 0, y: -75, x: -75 },
				{ autoAlpha: 1, scale: 1, x: 0, y: 0, delay: 0 }
			);

		Events.on('click', document.querySelector('.home__action__button__center'), (e) => {
			e.preventDefault();
			this._openingActionComplete();
		});

		const $body = document.querySelector('body');

		this._buttonEventIds.mouseOver = Events.on('mouseover', '.home__action__button__center', () => {
			this._scene.timeline.homeIntroHover();
			this._mouseController.homeIntroHover();
			clearTimeout(this._timeoutInt);
			this._timeoutInt = DelayHelper.setTimeout(() => this._view.textScramble.setText('Click'), 10);
			this.DOM.addClass($body, '-intromouseover');
			Simple.event('home_intro_mouse_over');
		});

		this._buttonEventIds.mouseOut = Events.on('mouseout', '.home__action__button__center', () => {
			this._scene.timeline.homeIntroMouseOut();
			this._mouseController.homeIntroMouseOut();
			clearTimeout(this._timeoutInt);
			this._timeoutInt = DelayHelper.setTimeout(() => this._view.textScramble.setText('Interact'), 10);
			this.DOM.removeClass($body, '-intromouseover');
			Simple.event('home_intro_mouse_out');
		});
	}

	private _openingActionComplete(): void {
		//Remove the intro button actions:
		if (this._openingActionClicked) {
			return;
		}

		globals.openingActionClicked = true;

		this._openingActionClicked = true;

		setTimeout(() => {
			this._pixiController.setAllowSlowFrameRateShow();
		}, 4000);

		this._scene.container.visible = true;
		SiteTransitions.introInteractionComplete();

		Events.offById(this._buttonEventIds.mouseOver);
		Events.offById(this._buttonEventIds.mouseOut);
		globals.touchActive = false;
		this._scene.timeline.introComplete();
		this._setInteractionEnabled(true);

		this._mouseController.introComplete();
		this._mouseController.setDragCursorActive(true);

		this.get('preload').hideForIntro();

		let cords: Point = this._mouseController.getMouseCords();
		let offsetx: number = cords.x - window.innerWidth / 2;
		let offsety: number = cords.y - window.innerHeight / 2;

		gsap.TweenMax.to('.home__action', 0.4, { autoAlpha: 0, scale: 0, y: -75 + offsety, x: -75 + offsetx });
		gsap.TweenMax.to('.home__action__text', 0.3, { autoAlpha: 0, ease: Config.linearEase });

		this._uiController.showUi(2000);

		//Add a history buffer on back for errors swipe back

		this._prompts.queueOpeningDragGuide();

		//Dev harness (/dev/home/, config/dev): the intro has just completed and
		//interaction unlocked, so this is the earliest point the whole home
		//state can be jumped to the configured department:
		if (dev.active) {
			this._devJumpToDepartment();
		}
	}

	/**
	 * DEV-HARNESS ONLY (config/dev — the /dev/home/ route). Instant-jump the
	 * home view to the configured department's drag REST position. Routes
	 * through the exact machinery the deeplink / back-from-work resets use
	 * (_resetXProgress = timeline.setProgress + drag.resetProgress + one
	 * instant timeline tick), so everything catches up in one frame: the
	 * region machine fires its progress-region enters (paging index, SCREENS/
	 * SPACES timeline state), the DepartmentSwitcher sees the new
	 * sceneProps.progress.ratio inside the band and starts the department's
	 * scene (startAsActiveScene + CHANGE_HOME_SCENE), and the drag's
	 * breakpoints progress matches the timeline — a following user drag
	 * continues smoothly from here rather than snapping back.
	 *
	 * The progress MUST be a drag breakpoint ratio (DragBreakpoints non-loop
	 * ratios for 4 points: 1/3, 2/3, 1 — the same constants _resetFromWork
	 * uses). An off-breakpoint value (this jump previously used the
	 * config/webgl-home sceneBand midpoints, e.g. 0.95 for systems) leaves
	 * the scene short of where a real drag settles, and the FIRST pointer
	 * release — even a movement-free click — runs DragBreakpoints' release
	 * snap to the nearest breakpoint, visibly shifting the whole department
	 * (~160px on the systems lattice at 1440w). Each rest ratio also falls
	 * inside its sceneBand, so the DepartmentSwitcher behaviour is unchanged.
	 */
	private _devJumpToDepartment(): void {
		const department = dev.activeDepartment;

		let progress = 0; //'home' = the rest position the deeplink flow already set
		if (department === 'screens') progress = 0.3333333;
		else if (department === 'spaces') progress = 0.6666666;
		else if (department === 'systems') progress = 1;

		if (progress === 0) {
			return;
		}

		this._resetXProgress(progress);
	}

	/** Animate in the work items from the top coming back from work (screens or spaces) view  */
	private _showFromWork(department: DepartmentType): void {
		//1.) Animate the elements from top:

		this._paging.showFromWork();

		this._timeouts.interaction = DelayHelper.setTimeout(() => {
			this.DOM.removeClass(document.querySelector('body'), 'in-transition');
		}, 3000);
	}

	public preActivateScenes() {
		this._active = true;
		this._scene.timeline.unfreeze();
		this._scene.container.visible = true;
		this._scene.container.alpha = 1;
		this.resize(true);
		this._resetFromWork(this._activeWorkDepartment);
	}

	public activateScenes() {
		console.log('activateScenes', this._activeWorkDepartment);
		this._scene.timeline.resetAnimationFreeze();
		this._scene.timeline.showFromWork(this._activeWorkDepartment);
		this._drag.setActive(true);
		this._setInteractionEnabled(true);
		ParticleBehaviourLookup.unpause();
	}

	public hidePixiContainer() {
		this._scene.container.visible = false;
		this._scene.container.alpha = 0;
	}

	//Hide methods
	//---------------------------------------------------------------------------------------------------------

	//** Hide to move to info section. Means hiding pixi and Home HTML elements */
	private _hideForInfo(): void {}

	//** Hide after moving to work. Not too much to do here apart from deactive and hide elements */
	private _hideForWork(department: DepartmentType): void {
		SiteTransitions.startHomeToWork(department);
	}

	public deactivateForWork() {
		this.DOM.removeClass(this.container, '-active');

		this._scene.container.visible = false;

		this._setupForWork();

		this._scene.timeline.setupForWork(this._activeWorkDepartment);

		this._drag.setActive(false);
		this._active = false;
		ParticleBehaviourLookup.pause();
	}
}
